import { ArgumentError } from "../core/exception/argument-error";
import { StringHelper } from "./string-helper";

export class Bit {
	public static or(a: number, b: number): number {
		a = Bit.checkedNumber(a);
		b = Bit.checkedNumber(b);

		let aAsBinaryStringLtH = a.toString(2).split("").reverse();
		let bAsBinaryStringLtH = b.toString(2).split("").reverse();
		let resultLtH = "";
		let maxLength = Math.max(aAsBinaryStringLtH.length, bAsBinaryStringLtH.length);
		for (let i = 0; i < maxLength; i++) {
			let left = aAsBinaryStringLtH.length >= i && aAsBinaryStringLtH[i] === "1";
			let right = bAsBinaryStringLtH.length >= i && bAsBinaryStringLtH[i] === "1";
			let curAnd = left || right ? "1" : "0";
			resultLtH = curAnd + resultLtH;
		}

		return parseInt(resultLtH, 2);
	}

	public static orAll(items: number[]): number {
		let result = 0;
		for (let item of items) {
			result = Bit.or(result, item);
		}
		return result;
	}

	public static and(a: number, b: number): number {
		a = Bit.checkedNumber(a);
		b = Bit.checkedNumber(b);
		let aAsBinaryStringLtH = a.toString(2).split("").reverse();
		let bAsBinaryStringLtH = b.toString(2).split("").reverse();
		let resultLtH = "";
		let minLength = Math.min(aAsBinaryStringLtH.length, bAsBinaryStringLtH.length);
		for (let i = 0; i < minLength; i++) {
			let left = aAsBinaryStringLtH[i] === "1";
			let right = bAsBinaryStringLtH[i] === "1";
			let curAnd = left && right ? "1" : "0";
			resultLtH = curAnd + resultLtH;
		}

		return parseInt(resultLtH, 2);
	}

	public static andAll(items: number[]): number {
		if (items.length === 0) {
			return 0;
		}

		let result = items[0];
		for (let i = 1; i < items.length; i++) {
			result = Bit.and(result, items[i]);
		}
		return result;
	}

	public static hasFlags(a: number, b: number): boolean {
		return Bit.and(a, b) !== 0;
	}

	public static hasAllFlags(a: number, b: number): boolean {
		return Bit.and(a, b) === b;
	}

	public static shiftLeft(a: number, shift: number): number {
		a = Bit.checkedNumber(a);
		shift = Bit.checkedNumber(shift);

		let aAsBinaryString = a.toString(2);

		aAsBinaryString += StringHelper.pad("", shift);
		return parseInt(aAsBinaryString, 2);
	}

	public static shiftRight(a: number, shift: number): number {
		a = Bit.checkedNumber(a);
		shift = Bit.checkedNumber(shift);

		let aAsBinaryString: string = a.toString(2);
		if (shift >= aAsBinaryString.length) {
			return 0;
		}
		return parseInt(aAsBinaryString.substr(0, aAsBinaryString.length - shift), 2);
	}

	/* tslint:disable-next-line */
	/**
	 * Internal method to negate a bit
	 * @param a bit
	 * @param length length to negative bit with
	 */
	public static _not(a: number, length: number) {
		a = Bit.checkedNumber(a);
		length = Bit.checkedNumber(length);

		let aAsBinaryStringWithLeadingZero: string[] = StringHelper.pad(a.toString(2), length).split("");
		let result = "";
		for (let item of aAsBinaryStringWithLeadingZero) {
			result += item === "1" ? "0" : "1";
		}

		return parseInt(result, 2);
	}

	public static andNot(a: number, b: number): number {
		a = Bit.checkedNumber(a);
		b = Bit.checkedNumber(b);
		let maxBitLength = Math.max(Bit.bitLength(a), Bit.bitLength(b));
		return Bit.and(a, Bit._not(b, maxBitLength));
	}

	public static xor(a: number, b: number): number {
		a = Bit.checkedNumber(a);
		b = Bit.checkedNumber(b);
		let maxBitLength = Math.max(Bit.bitLength(a), Bit.bitLength(b));

		let aAsBinaryStringWithLeadingZero: string[] = StringHelper.pad(a.toString(2), maxBitLength).split("");
		let bAsBinaryStringWithLeadingZero: string[] = StringHelper.pad(b.toString(2), maxBitLength).split("");
		let result = "";
		for (let i = 0; i < aAsBinaryStringWithLeadingZero.length; i++) {
			result += aAsBinaryStringWithLeadingZero[i] === bAsBinaryStringWithLeadingZero[i] ? "0" : "1";
		}
		return parseInt(result, 2);
	}

	public static mux(mask: number, a: number, b: number): number {
		mask = Bit.checkedNumber(mask);
		a = Bit.checkedNumber(a);
		b = Bit.checkedNumber(b);

		let t1 = Bit.andNot(a, mask);
		let t2 = Bit.and(b, mask);
		return Bit.or(t1, t2);
	}

	private static checkedNumber(bit: number): number {
		if (bit < 0) {
			throw new ArgumentError("bit", `The value should be greater or equal zero '${bit}'`);
		}
		if (bit >= Math.pow(2, 53)) {
			throw new ArgumentError("bit", `The value is to high for safe bit operation ${bit}`);
		}

		return bit || 0;
	}

	private static bitLength(bit: number): number {
		return bit.toString(2).length;
	}

	private constructor() {
	}
}